import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, CreditCard, Loader2 } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import {
  getBillingSummary,
  openBillingPortal,
  startBillingCheckout,
  type BillingInterval,
  type BillingPlanKey,
  type BillingSummary,
} from '@/lib/data'

function PanelHeading({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-5">
      <h2 className="font-display text-lg font-bold tracking-tight text-ink-900">{title}</h2>
      {description && <p className="mt-1 text-sm text-ink-500">{description}</p>}
    </div>
  )
}

const usdFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
const money = (cents: number) => usdFormatter.format(cents / 100)

function billingCaption(summary: BillingSummary, trialDays: number | null) {
  if (trialDays !== null && summary.subscription.plan === 'free') {
    return `${trialDays} trial day${trialDays === 1 ? '' : 's'} remaining`
  }
  if (summary.subscription.cancelAtPeriodEnd && summary.subscription.currentPeriodEnd) {
    return `Access ends ${new Date(summary.subscription.currentPeriodEnd).toLocaleDateString()}`
  }
  if (summary.subscription.status === 'canceled') return 'Subscription ended'
  if (summary.subscription.currentPeriodEnd) {
    return `Renews ${new Date(summary.subscription.currentPeriodEnd).toLocaleDateString()}`
  }
  return summary.subscription.status
}

function checkoutLabel(configured: boolean, canStartCheckout: boolean) {
  if (!configured) return 'Billing setup required'
  if (!canStartCheckout) return 'Change in billing portal'
  return 'Choose plan'
}

const defaultBillingApi = { getSummary: getBillingSummary, checkout: startBillingCheckout, portal: openBillingPortal }

export function BillingPanel({ api = defaultBillingApi }: { api?: typeof defaultBillingApi } = {}) {
  const [searchParams] = useSearchParams()
  const [summary, setSummary] = useState<BillingSummary | null>(null)
  const [interval, setInterval] = useState<BillingInterval>('monthly')
  const [busy, setBusy] = useState<BillingPlanKey | 'portal' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const intervalInitialized = useRef(false)
  const refreshSequence = useRef(0)
  const mounted = useRef(true)
  const latestSummary = useRef<BillingSummary | null>(null)

  const refresh = async (isCanceled = () => false) => {
    const sequence = ++refreshSequence.current
    const current = () => mounted.current && !isCanceled() && sequence === refreshSequence.current
    try {
      const next = await api.getSummary()
      if (!current()) return null
      setError(null)
      latestSummary.current = next
      setSummary(next)
      if (!intervalInitialized.current && next.subscription.interval) {
        intervalInitialized.current = true
        setInterval(next.subscription.interval)
      }
      return next
    } catch (e) {
      if (current()) setError(e instanceof Error ? e.message : 'Failed to load billing')
      return null
    }
  }

  useEffect(() => {
    mounted.current = true
    let canceled = false
    let timer: number | undefined
    const deadline = Date.now() + 12_000
    const poll = async () => {
      const next = await refresh(() => canceled)
      const displayed = next || latestSummary.current
      const awaitingWebhook =
        searchParams.get('checkout') === 'success' &&
        (!displayed || !displayed.subscription.hasPaidInvoice)
      if (!canceled && awaitingWebhook && Date.now() < deadline) {
        timer = window.setTimeout(poll, 1500)
      }
    }
    void poll()
    return () => {
      canceled = true
      mounted.current = false
      refreshSequence.current++
      if (timer) window.clearTimeout(timer)
    }
  }, [searchParams.get('checkout'), api])

  const checkout = async (plan: BillingPlanKey) => {
    setBusy(plan)
    setError(null)
    try {
      await api.checkout(plan, interval)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start checkout')
      setBusy(null)
    }
  }

  const portal = async () => {
    setBusy('portal')
    setError(null)
    try {
      await api.portal()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open billing')
      setBusy(null)
    }
  }

  if (!summary) {
    if (error) {
      return (
        <div>
          <PanelHeading title="Billing" description="Choose a plan and manage your subscription." />
          <div className="card p-5">
            <p className="text-sm font-semibold text-ink-900">Billing is temporarily unavailable</p>
            <p className="mt-1 text-sm text-ink-500">{error}</p>
            <button type="button" className="btn-subtle mt-4" onClick={() => void refresh()}>
              Try again
            </button>
          </div>
        </div>
      )
    }
    return (
      <p className="flex items-center gap-2 text-sm text-ink-400">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading billing...
      </p>
    )
  }

  const current = summary.catalog.plans.find((plan) => plan.key === summary.subscription.plan)
  const trialDays = summary.subscription.trialEndsAt
    ? Math.max(0, Math.ceil((summary.subscription.trialEndsAt - Date.now()) / 86_400_000))
    : null
  const checkoutState = searchParams.get('checkout')
  const canStartCheckout =
    !summary.subscription.hasSubscription || summary.subscription.status === 'canceled'

  return (
    <div>
      <PanelHeading title="Billing" description="Choose a plan and manage your subscription." />

      {checkoutState === 'success' && !summary.subscription.hasPaidInvoice && (
        <div className="mb-4 rounded-xl border border-brand-200 bg-brand-50 p-4 text-sm text-brand-700">
          Your payment is being confirmed. Credits appear after payment is complete.
          <button type="button" className="ml-2 underline" onClick={() => void refresh()}>Check again</button>
        </div>
      )}
      {checkoutState === 'canceled' && (
        <div className="mb-4 rounded-xl border border-line bg-panel p-4 text-sm text-ink-600">
          Checkout was canceled. Your current plan has not changed.
        </div>
      )}
      {summary.subscription.status === 'past_due' && (
        <div className="mb-4 flex gap-3 rounded-xl border border-accent-red/30 bg-accent-red/5 p-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-accent-red" />
          <div>
            <p className="text-sm font-semibold text-ink-900">Payment needs attention</p>
            <p className="mt-1 text-xs text-ink-500">
              Update your payment method before the grace period ends
              {summary.subscription.graceEndsAt
                ? ` on ${new Date(summary.subscription.graceEndsAt).toLocaleDateString()}`
                : ''}.
            </p>
          </div>
        </div>
      )}

      {summary.access?.internal && (
        <p role="status" className="mb-4 rounded-xl border border-line bg-panel p-4 text-sm text-ink-600">
          Internal access: plan and credit limits do not block this workspace. Usage is still recorded.
        </p>
      )}
      <div className="card mb-5 flex flex-wrap items-center justify-between gap-4 p-5">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-ink-400">Current plan</p>
          <p className="mt-1 font-display text-xl font-bold text-ink-900">
            {current?.label || (summary.subscription.plan === 'free' ? 'Trial' : summary.subscription.plan === 'legacy_pro' ? 'Legacy Pro' : summary.subscription.plan)}
          </p>
          <p className="mt-1 text-xs text-ink-500">
            {billingCaption(summary, trialDays)}
          </p>
        </div>
        {summary.canManage && summary.subscription.hasCustomer && (
          <button type="button" className="btn-subtle" onClick={portal} disabled={Boolean(busy)}>
            {busy === 'portal' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
            Manage billing
          </button>
        )}
      </div>

      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card p-4">
          <p className="text-xs text-ink-400">Connected channels</p>
          <p className="mt-1 text-lg font-bold text-ink-900">
            {summary.usage.accounts.used} / {summary.usage.accounts.limit ?? 'Unlimited'}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-ink-400">Team members</p>
          <p className="mt-1 text-lg font-bold text-ink-900">
            {summary.usage.members.used} / {summary.usage.members.limit ?? 'Unlimited'}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-ink-400">Posts this period</p>
          <p className="mt-1 text-lg font-bold text-ink-900">
            {summary.usage.posts.published + summary.usage.posts.pending} /{' '}
            {summary.usage.posts.limit ?? 'Unlimited'}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-ink-400">AI credits available</p>
          <p className="mt-1 text-lg font-bold text-ink-900">{summary.usage.ai.balance}</p>
          <p className="text-xs text-ink-400">{summary.usage.ai.usedThisPeriod} used this period</p>
        </div>
      </div>

      <div className="card mb-5 p-5">
        <h3 className="text-sm font-semibold text-ink-900">How credits work</h3>
        <p className="mt-2 text-sm text-ink-600">
          Each AI generation uses 1 credit. An agent request can use several generations.
          Failed generations return their credit. Unused credits carry over.
          Annual plans add credits monthly, including months when you do not use the app.
        </p>
        <p className="mt-2 text-sm text-ink-600">
          {summary.subscription.plan === 'free' ? 'Your trial starts with 10 credits. Onboarding rewards add to the same balance.' : `Your plan includes ${summary.usage.ai.monthlyGrant.toLocaleString()} credits per month.`}
          {summary.credits.nextGrantAt && ` Next credits: ${new Date(summary.credits.nextGrantAt).toLocaleDateString()}.`}
        </p>
        {summary.credits.paymentBlocked && <p role="status" className="mt-2 text-sm text-accent-red">
          Credit renewals are paused because this payment was refunded or disputed. Open billing for payment details.
        </p>}
        <p className="mt-2 text-xs text-ink-500">Full refunds remove unused credits from that payment. Partial refunds keep your credits. Disputed payments pause credit renewals until resolved.</p>
        {summary.credits.history.length > 0 && <details className="mt-4">
          <summary className="cursor-pointer text-sm font-medium text-ink-900">Recent credit activity</summary>
          <ul className="mt-2 divide-y divide-line">
            {summary.credits.history.map(item => <li key={item.id} className="flex items-center justify-between gap-3 py-2 text-sm">
              <span className="text-ink-600">{item.action === 'credit_grant' ? 'Credits added' : item.action === 'ai_credit_refund' ? 'Failed generation returned' : item.action === 'credit_payment_adjustment' ? 'Payment adjustment' : 'AI generation'} <span className="text-xs text-ink-400">{new Date(item.createdAt).toLocaleDateString()}</span></span>
              <span className="font-medium text-ink-900">{item.delta > 0 ? '+' : ''}{item.delta}</span>
            </li>)}
          </ul>
        </details>}
      </div>

      <div className="mb-4 inline-flex rounded-xl border border-line bg-panel p-1" aria-label="Billing interval">
        {(['monthly', 'annual'] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setInterval(value)}
            aria-pressed={interval === value}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              interval === value ? 'bg-surface text-ink-900 shadow-sm' : 'text-ink-500'
            }`}
          >
            {value === 'monthly' ? 'Monthly' : 'Annual'}
          </button>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {summary.catalog.plans.map((plan) => {
          const selected = summary.subscription.plan === plan.key && summary.subscription.interval === interval
          const cents = plan.prices[interval]
          return (
            <article key={plan.key} className={`card p-5 ${selected ? 'ring-2 ring-brand-300' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-lg font-bold text-ink-900">{plan.label}</h3>
                  <p className="mt-1 text-2xl font-bold text-ink-900">{money(cents)}</p>
                  <p className="text-xs text-ink-400">USD per {interval === 'monthly' ? 'month' : 'year'}, plus tax</p>
                </div>
                {selected && <span className="rounded-full bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-600">Current</span>}
              </div>
              <ul className="mt-4 space-y-1.5 text-sm text-ink-600">
                <li>{plan.limits.accounts} connected channels</li>
                <li>{plan.limits.members ?? 'Unlimited'} team member{plan.limits.members === 1 ? '' : 's'}</li>
                <li>Unlimited posts, subject to provider safety limits</li>
                <li>{plan.limits.monthlyAiCredits.toLocaleString()} AI credits per month</li>
                <li>API access included</li>
              </ul>
              {summary.canManage && !selected && (
                <button
                  type="button"
                  className="btn-primary mt-5 w-full justify-center"
                  onClick={() => canStartCheckout ? checkout(plan.key) : portal()}
                  disabled={Boolean(busy) || (canStartCheckout && !plan.configured)}
                >
                  {busy === plan.key && <Loader2 className="h-4 w-4 animate-spin" />}
                  {checkoutLabel(plan.configured, canStartCheckout)}
                </button>
              )}
            </article>
          )
        })}
      </div>
      {error && <p className="mt-4 text-sm text-accent-red">{error}</p>}
    </div>
  )
}
